<template>
  <div class="smart-debit-payment">
    <div>
      <CollectionDateSelect v-model="smartDebit.collectionDate" />
      <FormField
        :label="showAsteriskIfRequired('accountName') + $t('donationForm.details.smartDebit.accountName.label')"
        v-model="smartDebit.accountName"
        :validation="v$.smartDebit.accountName"
      />
      <div class="direct-debit-bank-details">
        <FormField
          class="smart-debit-account-number"
          :label="showAsteriskIfRequired('accountNumber') + $t('donationForm.details.smartDebit.accountNumber.label')"
          v-model="smartDebit.accountNumber"
          :validation="v$.smartDebit.accountNumber"
          v-maska="'########'"
        />
        <FormField
          class="smart-debit-sort-code"
          :label="showAsteriskIfRequired('sortCode') + $t('donationForm.details.smartDebit.sortCode.label')"
          v-model="smartDebit.sortCode"
          :validation="v$.smartDebit.sortCode"
          v-maska="'## ## ##'"
        />
      </div>
    </div>
    <div>
      <p>
        <small>{{ $t('donationForm.details.smartDebit.accountHolder.text-part-1') }}</small>
      </p>
      <div class="form-check">
        <input
          type="checkbox"
          id="account-holder-confirmed"
          v-model="accountHolderConfirmed"
          required
        >
        <label for="account-holder-confirmed">
          <span class="check" />
          <span class="text">{{ $t('donationForm.details.smartDebit.confirmationLabel') }}</span>
        </label>
      </div>
      <p>
        {{ $t('donationForm.details.smartDebit.accountHolder.text-part-2') }}
        <a
          :href="$t('donationForm.details.smartDebit.accountHolder.downloadableForm.url')"
          target="_blank"
        >
          {{ $t('donationForm.details.smartDebit.accountHolder.downloadableForm.text') }}
        </a>
      </p>
    </div>
    <div class="direct-debit-mandate-confirmation">
      <details>
        <summary>{{ $t('donationForm.details.smartDebit.directDebitGuarantee.title') }}</summary>
        <ul>
          <li
            v-for="item in $tm('donationForm.details.smartDebit.directDebitGuarantee.list')"
            :key="item"
          >
            {{ item }}
          </li>
        </ul>
      </details>
    </div>
    <SubmitButton @submitted="processDonation" />
  </div>
</template>

<script>
import { handleError, handleSuccess } from '@/lib/payment/handlers'
import awaitUpdate from '@/lib/donation/awaitUpdate'
import { vMaska } from 'maska/vue'
import { ref } from 'vue'
import { required, minLength } from '@vuelidate/validators'
import { useDonationStore } from '@/stores/donation'
import { usePaymentStore } from '@/stores/payment'
import { useRootStore } from '@/stores/root'
import { v4 as uuid } from 'uuid'
import axios from 'axios'
import CollectionDateSelect from '@/components/donation/CollectionDateSelect'
import FormField from '@/components/form/FormField'
import SubmitButton from '@/components/form/SubmitButton'
import useVuelidate from '@vuelidate/core'

export default {
  name: 'SmartdebitComponent',
  components: { FormField, CollectionDateSelect, SubmitButton },
  props: {
    validateFields: { type: Boolean, default: false }
  },
  directives: { maska: vMaska },
  setup () {
    const rootStore = useRootStore()
    const donationStore = useDonationStore()
    const paymentStore = usePaymentStore()
    const smartDebit = ref({ accountName: '', sortCode: '', accountNumber: '', collectionDate: '1' })
    const accountHolderConfirmed = ref(false)
    return { donationStore, paymentStore, rootStore, v$: useVuelidate(), smartDebit, accountHolderConfirmed }
  },
  validations: {
    smartDebit: {
      accountName: {
        required,
        minLength: minLength(3)
      },
      accountNumber: {
        required,
        minLength: minLength(8)
      },
      sortCode: {
        required,
        minLength: minLength(8) // 6 +2 from input mask
      }
    }
  },
  methods: {
    async processDonation () {
      const paymentDetails = {
        adapter: 'smartdebit',
        sort_code: this.paymentStore.details.sortCode.replace(/-|\s/g, ''),
        account_number: this.paymentStore.details.accountNumber,
        account_name: this.paymentStore.details.accountName,
        chosen_day: this.paymentStore.details.collectionDate
      }
      const params = Object.assign({}, {
        ...this.donationStore.donationParams,
        payment: { ...paymentDetails },
        location: this.$l10n.currentLocation.key,
        language: this.$l10n.currentLanguage,
        turnstile_response: this.rootStore.turnstileToken
      })
      let donationResponse = null
      try {
        donationResponse = await axios.post(`${process.env.VUE_APP_BACKEND_HOST}/donation`, params)
      } catch (error) {
        const failureMessage = error.response ? error.response.data : error
        const errMessage = (('message' in failureMessage) ? failureMessage.message : failureMessage)
        handleError(this, errMessage)
        return
      }

      const result = await awaitUpdate(this, donationResponse.data.reference)
      if (result.status === 'failed') {
        handleError(this, result.details)
        return
      }
      handleSuccess(this, this.thankYouPath)
    },
    showAsteriskIfRequired (field) {
      const fieldRequired = this.v$.smartDebit[field].required
      if (this.v$.smartDebit[field] === 'none') {
        return '* '
      }
      return (fieldRequired && fieldRequired.$invalid) ? '* ' : ''
    }
  },
  computed: {
    smartDebitValid () { return this.v$['smartDebit'] ? !this.v$.smartDebit.$invalid : false },
    frequency () { return this.donationStore.type },
    thankYouPath () { return this.$t('routeNames.thankYou') }
  },
  watch: {
    smartDebitValid: function (val) {
      this.paymentStore.setComplete(val && this.accountHolderConfirmed)
    },
    accountHolderConfirmed: function (val) {
      this.paymentStore.setComplete(val && this.smartDebitValid)
    },
    smartDebit: {
      handler() {
        this.paymentStore.setDetails({ ...this.smartDebit })
      },
      deep: true
    },
    validateFields: function (_freq) { this.v$.$touch() },
    frequency: function () { this.smartDebit.collectionDate = '1' }
  },
  mounted () {
    this.donationStore.setType('monthly')
    this.rootStore.setHiddenFrequencies(['oneOff'])
    this.donationStore.setReference(uuid().substring(0, 18))
    this.paymentStore.setComplete(false)
  },
  beforeUnmount () {
    this.rootStore.setHiddenFrequencies([])
  }
}
</script>

<style lang='scss' scoped>
  .smart-debit-payment {
    a {
      color: white;
    }
  }

  .direct-debit-bank-details {
    @include on-non-mobile(){
      display: grid;
      grid-auto-flow: column;
      grid-gap: 1rem;
    }
  }

  details {
    summary {
      font-weight: bold;
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .form-check {
    padding: 1rem;
    border: solid white 1px;
  }
</style>
